import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Separator, FlexView } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import {
  useGetFilters,
  usePostFilter,
  usePatchFilter,
  invalidateFilters,
} from 'actions/support-agent';
import {
  SavedFiltersSection,
  FilterDropdownWrap,
  FilterDropdownTrigger,
  SA_COLORS,
} from '../../../styled-comps';
import { FilterErrorMsg } from '../styled';
import { ERROR_AUTO_CLEAR_MS } from '../../../constants';
import FilterDropdown from './filter-dropdown';
import FacetBarSection from './facet-bar';
import Icon from '../../icon';

// mine/unassigned/bot all predicate on assigned_agent_id, same as the Agent
// facet, so ANDing them with a specific agent is unsatisfiable.
const ASSIGNEE_SCOPED_MODES = new Set(['mine', 'unassigned', 'bot']);

const InboxFilters = ({ onQueryChange, totalTickets, loadMoreRef }) => {
  const [filterId, setFilterId] = useState(null);
  const [page, setPage] = useState(1);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterSearchQuery, setFilterSearchQuery] = useState('');
  const [facet, setFacet] = useState(null);
  const [applied, setApplied] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedName, setSavedName] = useState('');
  const [renamingFilterId, setRenamingFilterId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [filterError, setFilterError] = useState(null);

  const filterDropdownRef = useRef(null);
  const patchActionRef = useRef({ action: null, deletedId: null });

  // Every path that switches the selected view (dropdown pick, post-delete
  // fallback, reconciliation on load) must clear the previous view's
  // per-view state, or facets/popover from the old view leak into the new one.
  const selectView = useCallback(id => {
    setFilterId(id);
    setPage(1);
    setApplied([]);
    setFacet(null);
  }, []);

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!showFilterDropdown) return;
    const handler = e => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setShowFilterDropdown(false);
        setFilterSearchQuery('');
        setFilterError(null);
        setRenamingFilterId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilterDropdown]);

  // Auto-clear filter error
  useEffect(() => {
    if (!filterError) return;
    const timer = setTimeout(() => setFilterError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [filterError]);

  // --- API hooks ---
  const {
    data: filtersData,
    get: getFilters,
    isProcessingFailed: filtersGetFailed,
  } = useGetFilters();

  const { post: postFilter, isProcessing: savingFilter } = usePostFilter({
    onComplete: error => {
      if (!error) {
        invalidateFilters();
        getFilters('filters');
        setSaving(false);
        setSavedName('');
        setFilterError(null);
      } else {
        setFilterError(error || 'Failed to save filter. Please try again.');
      }
    },
  });

  const { patch: patchFilter } = usePatchFilter({
    onComplete: error => {
      const { action, deletedId } = patchActionRef.current;
      patchActionRef.current = { action: null, deletedId: null };
      if (error) {
        const defaultMsg =
          action === 'rename'
            ? 'Failed to rename filter. Please try again.'
            : 'Failed to delete filter. Please try again.';
        setFilterError(error || defaultMsg);
        return;
      }
      setFilterError(null);
      if (action === 'rename') {
        invalidateFilters();
        getFilters('filters');
        setRenamingFilterId(null);
        setRenameValue('');
      } else if (action === 'delete') {
        invalidateFilters();
        getFilters('filters');
        if (filterId === deletedId) {
          const nextFilter = savedFilters.find(f => f.id !== deletedId);
          if (nextFilter) {
            selectView(nextFilter.id);
          }
        }
      }
    },
  });

  useEffect(() => {
    getFilters('filters');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Derived data ---
  const savedFilters = useMemo(() => {
    const filters = [];
    const defaults = filtersData?.default_filters;
    if (Array.isArray(defaults) && defaults.length > 0) {
      defaults.forEach(f => {
        filters.push({ id: f.name, label: f.name, params: f.filters || {} });
      });
    }
    const custom = filtersData?.saved_filters;
    if (Array.isArray(custom) && custom.length > 0) {
      custom.forEach(f => {
        filters.push({
          id: f.id,
          label: f.name,
          params: f.filters || f.params || {},
          isCustom: true,
        });
      });
    }
    return filters;
  }, [filtersData]);

  useEffect(() => {
    if (
      savedFilters.length > 0 &&
      (!filterId || !savedFilters.find(f => f.id === filterId))
    ) {
      selectView(savedFilters[0].id);
    }
  }, [savedFilters, selectView]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtersLoaded = savedFilters.length > 0;
  const activeFilter = filtersLoaded
    ? savedFilters.find(f => f.id === filterId) || savedFilters[0]
    : null;

  const dynamicFacets = useMemo(() => {
    const items = filtersData?.filter_options;
    if (!Array.isArray(items) || items.length === 0) return [];
    const assignedTo = activeFilter?.params?.assigned_to;
    const agentFacetDisabled = ASSIGNEE_SCOPED_MODES.has(assignedTo);
    return items.map(f => {
      const isDisabledAgentFacet = f.key === 'assigned_agent_id' && agentFacetDisabled;
      return {
        key: f.key,
        label: f.display_name,
        options: (f.options || []).map(opt => ({
          label: opt.name || opt.label || opt.value || opt.id,
          value: opt.value || opt.id,
        })),
        disabled: isDisabledAgentFacet,
        disabledReason: isDisabledAgentFacet
          ? "Agent doesn't apply to this view"
          : undefined,
      };
    });
  }, [filtersData, activeFilter]);

  const filteredSavedFilters = savedFilters.filter(f =>
    f.label.toLowerCase().includes(filterSearchQuery.toLowerCase()),
  );
  const activeFilterParamsKey = JSON.stringify(activeFilter?.params || {});

  const queryParams = useMemo(() => {
    if (!activeFilter) return null;
    const facetKeys = new Set(applied.map(a => a.facet));
    const base = Object.fromEntries(
      Object.entries(JSON.parse(activeFilterParamsKey)).filter(
        ([key]) => !facetKeys.has(key),
      ),
    );
    const params = { page, limit: 50, ...base };
    applied.forEach(({ facet: facetKey, value }) => {
      if (params[facetKey]) {
        params[facetKey] = Array.isArray(params[facetKey])
          ? [...params[facetKey], value]
          : [params[facetKey], value];
      } else {
        params[facetKey] = value;
      }
    });
    return params;
  }, [activeFilterParamsKey, applied, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onQueryChange(queryParams, page);
  }, [queryParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose load-more to parent via ref
  useEffect(() => {
    if (loadMoreRef) {
      loadMoreRef.current = () => setPage(prev => prev + 1);
    }
  }, [loadMoreRef]);

  // --- Handlers ---
  const facetAppliedCount = useCallback(
    facetKey => applied.filter(a => a.facet === facetKey).length,
    [applied],
  );
  const isFacetOptionApplied = useCallback(
    (facetKey, value) => applied.some(a => a.facet === facetKey && a.value === value),
    [applied],
  );

  const handleSelectFilter = useCallback(
    id => {
      selectView(id);
      setShowFilterDropdown(false);
      setFilterSearchQuery('');
      setRenamingFilterId(null);
    },
    [selectView],
  );

  const handleStartRename = useCallback((e, f) => {
    e.stopPropagation();
    setRenamingFilterId(f.id);
    setRenameValue(f.label);
    setFilterError(null);
  }, []);

  const handleConfirmRename = useCallback(() => {
    if (renamingFilterId && renameValue.trim()) {
      setFilterError(null);
      patchActionRef.current = { action: 'rename', deletedId: null };
      patchFilter(renamingFilterId, { name: renameValue.trim() });
    }
  }, [renamingFilterId, renameValue, patchFilter]);

  const handleDeleteFilter = useCallback(
    (e, filtId) => {
      e.stopPropagation();
      setFilterError(null);
      patchActionRef.current = { action: 'delete', deletedId: filtId };
      patchFilter(filtId, { is_deleted: true });
    },
    [patchFilter],
  );

  const handleToggleFacetOption = useCallback((facetKey, label, value) => {
    setApplied(prev => {
      const exists = prev.some(a => a.facet === facetKey && a.value === value);
      if (exists) return prev.filter(a => !(a.facet === facetKey && a.value === value));
      return [...prev, { facet: facetKey, label, value }];
    });
    setPage(1);
  }, []);

  const handleRemoveApplied = useCallback((facetKey, value) => {
    setApplied(prev => prev.filter(a => !(a.facet === facetKey && a.value === value)));
    setPage(1);
  }, []);

  const handleSubmitSave = useCallback(() => {
    if (savedName.trim() && !savingFilter && queryParams) {
      setFilterError(null);
      const { page: _p, limit: _l, ...filters } = queryParams;
      postFilter({ name: savedName.trim(), filters });
    }
  }, [savedName, savingFilter, queryParams, postFilter]);

  return (
    <SavedFiltersSection>
      {filtersGetFailed ? (
        <FilterErrorMsg>Failed to load filters. Please try again.</FilterErrorMsg>
      ) : !filtersLoaded ? (
        <Text align="center" variant="body2">
          Loading...
        </Text>
      ) : (
        <>
          <FilterDropdownWrap ref={filterDropdownRef}>
            <FilterDropdownTrigger onClick={() => setShowFilterDropdown(prev => !prev)}>
              <Text variant="body2">{activeFilter.label}</Text>
              <FlexView flexDirection="row" alignItems="center">
                <Text
                  marginHorizontal="8px 0px"
                  variant="body3"
                  color={SA_COLORS.TEXT_TERTIARY}
                >
                  ({totalTickets})
                </Text>
                <Icon name="chev" size={14} />
              </FlexView>
            </FilterDropdownTrigger>
            {showFilterDropdown && (
              <FilterDropdown
                filters={filteredSavedFilters}
                activeId={filterId}
                totalTickets={totalTickets}
                filterError={filterError}
                searchQuery={filterSearchQuery}
                onSearchChange={setFilterSearchQuery}
                renamingId={renamingFilterId}
                renameValue={renameValue}
                onSelect={handleSelectFilter}
                onStartRename={handleStartRename}
                onConfirmRename={handleConfirmRename}
                onCancelRename={() => setRenamingFilterId(null)}
                onRenameChange={setRenameValue}
                onDelete={handleDeleteFilter}
              />
            )}
          </FilterDropdownWrap>

          <Separator height={8} transparent />

          <FacetBarSection
            facets={dynamicFacets}
            applied={applied}
            activeFacet={facet}
            saving={saving}
            savedName={savedName}
            savingFilter={savingFilter}
            filterError={filterError}
            facetAppliedCount={facetAppliedCount}
            isFacetOptionApplied={isFacetOptionApplied}
            onToggleFacet={key => setFacet(prev => (prev === key ? null : key))}
            onCloseFacet={() => setFacet(null)}
            onToggleOption={handleToggleFacetOption}
            onRemoveApplied={handleRemoveApplied}
            onClearAll={() => {
              setApplied([]);
              setPage(1);
            }}
            onStartSave={() => setSaving(true)}
            onSaveNameChange={setSavedName}
            onCancelSave={() => {
              setSaving(false);
              setSavedName('');
              setFilterError(null);
            }}
            onSubmitSave={handleSubmitSave}
          />
        </>
      )}
    </SavedFiltersSection>
  );
};

export default InboxFilters;
